import React from "react";
import { MdLinkOff } from "react-icons/md";
import { InformationCircle, CheckCircle } from "../../../../components/icons/svg/outline";
import { convert24To12String, removeLeadingZeros } from "../../../../utils/datetime";
import { updateShiftField } from "../../../../utils";
import ConflictTooltip from "../../../../components/common/tooltip/ConflictTooltip";

/**
 * Checks whether the shift has no worker-recorded payable hours.
 *
 * @param {Object} shift - Payout timesheet shift row.
 * @returns {boolean} True when worker RT, OT, and DT are all zero.
 */
const hasNoWorkerHours = (shift) => {
  const workerRegular = Number(shift.total_rt ?? shift.regularTime ?? 0);
  const workerOvertime = Number(shift.total_ot ?? shift.overtime ?? 0);
  const workerDoubleTime = Number(shift.total_dt ?? shift.doubleTime ?? 0);

  return workerRegular === 0 && workerOvertime === 0 && workerDoubleTime === 0;
};

/**
 * TimeColumn component for displaying start or finish time with conflict detection
 * Handles both editing and non-editing modes with conflict tooltips
 */
const TimeColumn = ({
  shift,
  timeType, // 'start' or 'finish'
  isEditing,
  isReopenedReview = false,
  bgColor,
  dayIndex,
  shiftIndex,
  setShiftsToRender,
  className = ""
}) => {
  const isStartTime = timeType === 'start';
  
  // Determine field names based on time type
  const timeField = isStartTime ? 'startTime' : 'finishTime';
  const timeHoursLogField = isStartTime ? 'startTimeHoursLog' : 'finishTimeHoursLog';
  const timePayoutField = isStartTime ? 'startTimePayout' : 'finishTimePayout';
  
  // Get values from shift
  const currentTime = shift[timeField];
  const hoursLogTime = shift[timeHoursLogField];
  const payoutTime = shift[timePayoutField];
  const tooltipShift = hasNoWorkerHours(shift)
    ? { ...shift, startTime: null, finishTime: null }
    : shift;
  
  // Check if this is a payout-added shift (no id)
  const isPayoutAddedShift = shift.id === null || shift.id === undefined;
  
  // Detect conflicts (only if not a payout-added shift)
  const hasConflict = !isPayoutAddedShift && currentTime !== hoursLogTime;
  const hasPayoutConflict = payoutTime !== currentTime;

  // Handle time input changes
  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    setShiftsToRender((prevData) =>
      updateShiftField(
        prevData,
        dayIndex,
        shiftIndex,
        timePayoutField,
        newTime
      )
    );
  };

  // Handle time selection from conflict tooltip
  const handleTimeSelect = (selectedTime) => {
    setShiftsToRender((prevData) =>
      updateShiftField(
        prevData,
        dayIndex,
        shiftIndex,
        timePayoutField,
        selectedTime
      )
    );
  };

  // Get text color class based on conflicts
  const getTextColorClass = () => {
    // Payout-added shifts always show blue text
    if (isPayoutAddedShift) {
      return "text-blue-600";
    }
    if (hasPayoutConflict) {
      return "text-blue-600";
    }
    if (!hasConflict && hoursLogTime && isEditing) {
      return "text-black";
    }
    return "";
  };

  // Render conflict/status icons for editing mode
  const renderStatusIcon = () => {
    if (!isEditing) return null;
    
    // Hide hours log related icons for payout-added shifts, but maintain spacing
    if (isPayoutAddedShift) {
      return <div className="h-4 min-w-[22px] lg:text-lg lg:min-w-[22px]" />; // Invisible placeholder
    }

    if (hasConflict) {
      return shift.missingLogs ? (
        <InformationCircle className="h-4 lg:text-lg translate-x-[2px] text-yellow-600" />
      ) : (
        <MdLinkOff className="h-4 text-lg translate-x-1 text-red-600" />
      );
    }
    
    if (hoursLogTime && isEditing) {
      return <CheckCircle className="h-4 min-w-[22px] lg:text-lg text-green-600" />;
    }
    
    return null;
  };

  const cellClasses = `col-span-4 ${bgColor} py-3 px-1 border-b border-l border-gray-300 flex items-center justify-center text-sm h-12 ${
    isEditing ? "bg-white" : ""
  } relative group ${getTextColorClass()} ${className}`;

  return (
    <div className={cellClasses}>
      {isEditing ? (
        <div className="flex items-center justify-center w-full relative">
          {renderStatusIcon()}
          <input
            type="time"
            value={payoutTime || ""}
            className={`w-full h-8 text-center border-0 bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-300 rounded ${getTextColorClass()} cursor-pointer`}
            onChange={handleTimeChange}
          />
        </div>
      ) : (
        <span
          id={`${timeType}-${dayIndex}-${shiftIndex}`}
          className={getTextColorClass()}
        >
          {convert24To12String(removeLeadingZeros(payoutTime))}
        </span>
      )}

      {/* Conflict Tooltip - only show for non-payout-added shifts */}
      {!isPayoutAddedShift && (
        <ConflictTooltip
          isReopenedReview={isReopenedReview}
          hasConflict={hasConflict}
          shift={tooltipShift}
          timeType={timeType}
          fieldType="time"
          isEditing={isEditing}
          dayIndex={dayIndex}
          shiftIndex={shiftIndex}
          onValueSelect={isEditing ? handleTimeSelect : null}
        />
      )}
    </div>
  );
};

export default TimeColumn;